<template>
  <v-card no-body class="mb-3">
    <b-modal ref="infoModal" ok-only :size="infoModalConfig.size">
      <template v-slot:modal-title>
        <h5>{{ infoModalConfig.name }}</h5>
      </template>
      <template v-slot:default>
        <div v-html="infoModalConfig.infoText"></div>
      </template>
    </b-modal>
    <v-card-title class="customer-card-header" :header-bg-variant="headerBgVariant" :header-text-variant="headerTextVariant">
      {{ customerReport.name }} -
      <span class="font-weight-normal">
        <multi-time-display :time="startDate" time-format="ll" class="d-inline"/>
        {{ $t('to') }}
        <multi-time-display :time="endDate" time-format="ll" class="d-inline"/>
      </span>
    </v-card-title>
    <v-card-text>
      <v-row class="mt-2">
        <v-col class="mb-3">
          <v-row>
            <v-col>
              <v-card no-body :border-variant="cardBorderVariant">
                <v-card-title class="section-card-header" :header-bg-variant="cardHeaderBgVariant" header-text-variant="white">
                  <span class="info-text">{{ $t('reports_ui.route_data_quality_report.overall_summary') }}</span>
                  <font-awesome-icon class="info-icon" icon="info-circle" @click="showInfoModal(modalConfigs.overallSummary)"/>
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col>
                      <div>{{ $t('total_routes') }}: {{ customerReport.numRoutes }}</div>
                      <div>{{ $t('high_quality_routes') }}: {{ customerReport.highQualityRoutes  }}</div>
                      <div>{{ $tc('routes_with_issues') }}: {{ routesWithIssuesCount }}</div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-card no-body :border-variant="cardBorderVariant" class="mt-3">
                <v-card-title class="section-card-header" :header-bg-variant="cardHeaderBgVariant" header-text-variant="white">
                  <span class="info-text">{{ $t('reports_ui.route_data_quality_report.route_product_summary') }}</span>
                  <font-awesome-icon class="info-icon" icon="info-circle" @click="showInfoModal(modalConfigs.routeProductSummary)"/>
                </v-card-title>
                <v-simple-table striped hover class="card-table" v-if="!isEmpty(productStats)">
                  <b-thead>
                    <tr>
                      <th>{{ $tc('product') }}</th>
                      <th>{{ $tc('product_issue', 2) }}</th>
                    </tr>
                  </b-thead>
                  <b-tbody>
                    <tr v-for="(product, index) in productStats" :key="index">
                      <td><router-link :to="'/products/' + product.id">{{ product.name }}</router-link></td>
                      <td>{{ product.routes.length }}</td>
                    </tr>
                  </b-tbody>
                </v-simple-table>
                <div v-else>
                  <v-card-text>{{ $t('no_results') }}</v-card-text>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
        <v-col class="mb-3">
          <v-card no-body :border-variant="cardBorderVariant">
            <v-card-title class="section-card-header" :header-bg-variant="cardHeaderBgVariant" header-text-variant="white">
              <span class="info-text">{{ $t('reports_ui.route_data_quality_report.top_10_vehicles') }}</span>
              <font-awesome-icon class="info-icon" icon="info-circle" @click="showInfoModal(modalConfigs.top10Vehicles)"/>
            </v-card-title>
              <v-simple-table striped hover class="card-table" v-if="!isEmpty(topTenVehicles)">
                <b-thead>
                  <tr>
                    <th>{{ $tc('vehicle') }}</th>
                    <th>{{ $t('routes_with_issues') }}</th>
                    <th>{{ $t('total_routes_run') }}</th>
                  </tr>
                </b-thead>
                <b-tbody>
                  <tr v-for="(vehicle, index) in topTenVehicles" :key="index">
                    <td class="text-truncate">
                     <router-link :to="'/vehicle/' + vehicle.id"><font-awesome-icon icon="truck" class="mr-1"/>{{ vehicle.name }}</router-link>
                    </td>
                    <td>{{ vehicle.routes.length }}</td>
                    <td>{{ vehicle.totalRoutes }}</td>
                  </tr>
                </b-tbody>
              </v-simple-table>
              <div v-else>
                <v-card-text>{{ $t('no_results') }}</v-card-text>
              </div>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="mb-3">
          <v-card no-body :border-variant="cardBorderVariant">
            <v-card-title class="section-card-header" :header-bg-variant="cardHeaderBgVariant" header-text-variant="white">
              <span class="info-text">{{ $t('reports_ui.route_data_quality_report.quality_issue_distribution') }}</span>
              <font-awesome-icon class="info-icon" icon="info-circle" @click="showInfoModal(modalConfigs.qualityIssueDistribution)"/>
            </v-card-title>
            <v-simple-table striped hover class="card-table" v-if="!isEmpty(sortedFlagCounts)">
              <b-thead>
                <tr>
                  <th>{{ $tc('issue' )}}</th>
                  <th>{{ $tc('category') }}</th>
                  <th>{{ $t('number_of_routes') }}</th>
                </tr>
              </b-thead>
              <b-tbody>
                <tr v-for="(entry, index) of sortedFlagCounts" :key="index">
                  <td>{{ getFlagLabel(entry.flag) }}</td>
                  <td>{{ $tc('reports_ui.route_data_quality_report.flag_categories.' + getFlagCategory(entry.flag)) }}</td>
                  <td>{{ entry.routes.length }}</td>
                </tr>
              </b-tbody>
            </v-simple-table>
            <div v-else>
              <v-card-text>{{ $t('no_results') }}</v-card-text>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import ReportRecord from '@/orm/models/ReportRecord'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay.vue'
import Moment from 'moment-timezone'
import { get, toLower, take, sortBy, map, reverse, isEmpty, keys } from 'lodash'

export default {
  name: 'RouteDataQualityReportCard.vue',
  components: {
    MultiTimeDisplay
  },
  props: {
    customerReport: {
      type: Object
    },
    Date: {
      type: String
    },
    startDate: {
      type: String
    },
    endDate: {
      type: String
    },
    timezone: {
      type: String
    },
    comparison: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      infoModalConfig: {}
    }
  },
  computed: {
    headerBgVariant () {
      return this.comparison ? 'secondary' : 'light'
    },
    headerTextVariant () {
      return this.comparison ? 'white' : 'normal'
    },
    cardHeaderBgVariant () {
      return this.comparison ? 'dark' : 'primary'
    },
    cardBorderVariant () {
      return this.comparison ? 'dark' : 'primary'
    },
    routesWithIssuesCount () {
      return keys(this.customerReport.routesWithIssues).length
    },
    topTenVehicles () {
      const vehiclesArray = map(this.customerReport.vehicleStats, (vehicle, id) => {
        return { id: id, name: vehicle.name, routes: vehicle.routes, totalRoutes: vehicle.totalRoutes }
      })
      return take(reverse(sortBy(vehiclesArray, x => x.routes.length)), 10)
    },
    sortedFlagCounts () {
      const flagsArray = map(this.customerReport.flagCounts, (routes, flag) => {
        return { flag: flag, routes: routes }
      })
      return reverse(sortBy(flagsArray, x => x.routes.length))
    },
    productStats () {
      return map(this.customerReport.productStats, (product, id) => {
        return { id: id, name: product.name, routes: product.routes }
      })
    },
    modalConfigs () {
      return {
        overallSummary: {
          size: 'lg',
          name: this.$t('reports_ui.route_data_quality_report.overall_summary'),
          infoText: this.$t('reports_ui.route_data_quality_report.modal_info.overall_summary')
        },
        routeProductSummary: {
          size: 'lg',
          name: this.$t('reports_ui.route_data_quality_report.route_product_summary'),
          infoText: this.$t('reports_ui.route_data_quality_report.modal_info.route_product_summary')
        },
        top10Vehicles: {
          size: 'lg',
          name: this.$t('reports_ui.route_data_quality_report.top_10_vehicles'),
          infoText: this.$t('reports_ui.route_data_quality_report.modal_info.top_10_vehicles')
        },
        qualityIssueDistribution: {
          size: 'lg',
          name: this.$t('reports_ui.route_data_quality_report.quality_issue_distribution'),
          infoText: this.$t('reports_ui.route_data_quality_report.modal_info.quality_issue_distribution')
        }
      }
    }
  },
  methods: {
    isEmpty: isEmpty,
    keys: keys,
    getFlagLabel (flag) {
      return this.$t('reports_ui.route_data_quality_report.flag_labels.' + toLower(flag))
    },
    /**
     * Load the selected modal config and show the info modal.
     */
    showInfoModal (config) {
      this.infoModalConfig = config
      this.$refs.infoModal.show()
    },
    getFlagCategory (flag) {
      switch (flag) {
        case 'QUALITY_UNASSESSED':
          return 'none'
        case 'DURATION_TOO_SHORT':
        case 'DURATION_TOO_LONG':
        case 'OVERLAPPING_ROUTE':
          return 'route_duration'
        case 'START_AFTER_DC_DEPARTURE':
        case 'END_BEFORE_LAST_KNOWN_STOP':
          return 'user_modified_route'
        case 'NO_DELIVERY_STOPS':
        case 'MISSED_STOPS':
        case 'STOPPED_AT_UNPLANNED_LOCATION':
          return 'unusual_delivery_stops'
        case 'COOLER_SENSOR_FAILURE':
        case 'FREEZER_SENSOR_FAILURE':
        case 'GPS_SENSOR_FAILURE':
          return 'telemetry_failure'
        case 'FUTURE_TELEMETRY':
        case 'TELEMETRY_REPORTED_LATE':
        case 'ABNORMAL_AMBIENT_TEMPERATURE':
          return 'unusual_telemetry'
        case 'START_TEMPERATURE_MODIFIED':
        case 'ABNORMAL_START_TEMPERATURE':
          return 'route_product'
      }
    }
  }
}
</script>

<style scoped>
  .customer-card-header {
    font-weight: bold;
    font-size: 1.1rem;
  }
  .section-card-header {
    font-weight: bold;
    font-size: 1rem;
    padding: 0.6rem;
  }
  .info-icon {
    cursor: pointer;
    justify-content: flex-end;
    align-self: center;
    margin-left: .5rem;
  }
  .card-table th, .card-table td{
    padding: 0.3rem 1rem;
  }
</style>
